import { CommonApiResponse } from "../../Controllers/Types/ApiResponse";
import { SiteVisitReport } from "../../Controllers/Types/SiteVisitReport/SiteVisitReportType";
import apiClient from "../apiClient";
import { baseURL } from "../ApiConstant";

// Get Site visit report data
export const getSiteVisitReportByFolderId = async (folder_id: number) => {
    try {
        const response = await apiClient.get(`/api/site-visit-report/list/${folder_id}`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

// Get weather list
export const getWeatherList = async () => {
    try {
        const response = await apiClient.get(`/api/site-visit-report/weather-list`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

/* ******************************************************************* Building inspector */
interface SiteVisitReportCreateResponse {
    status: boolean;
    statusCode?: number;
    massage?: string;
    error?: string;
    errors?: any[];
    errorInfo?: any[];
    data?: SiteVisitReport | null;
}

// Create new site visit report
export const createSiteVisitReportAPI = async (
    folder_id: number,
    weather_id: number,
    date: string,
    inspection_for: string,
): Promise<SiteVisitReportCreateResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit-report/bi/create`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                folder_id,
                weather_id,
                date,
                inspection_for
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return {
                status: true,
                statusCode: 200,
                data: data['data']
            };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Update site visit report
export const updateSiteVisitReportAPI = async (
    site_visit_report_id: number,
    weather_id: number,
    date: string,
    inspection_for: string,
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit-report/bi/update/${site_visit_report_id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                weather_id,
                date,
                inspection_for
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete site visit report
export const deleteSiteVisitReportAPI = async (
    site_visit_report_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit-report/bi/delete/${site_visit_report_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Create site visit report note
export const createSiteVisitReportNote = async (
    site_visit_id: number,
    note_no: string,
    note: string,
    status: string,
    file_list: File[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();
        formData.append("site_visit_id", String(site_visit_id));
        formData.append("note_no", note_no);
        formData.append("note", note);
        formData.append("status", status);

        if (file_list && file_list.length > 0) {
            file_list.forEach((file) => {
                formData.append("file_list[]", file);
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit-report/bi/note-create`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );
        const data = await response.json();
        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Update site visit report note
export const updateSiteVisitReportNote = async (
    note_id: number,
    site_visit_id: number,
    note: string,
    status: string,
    file_list: File[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();

        formData.append("site_visit_id", String(site_visit_id));
        formData.append("note", note);
        formData.append("status", status);

        if (file_list && file_list.length > 0) {
            file_list.forEach((file) => {
                formData.append("file_list[]", file);
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit-report/bi/note-update/${note_id}`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete site visit report note
export const deleteSiteVisitReportNote = async (
    note_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit-report/bi/note-delete/${note_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete site visit report note file
export const deleteSiteVisitReportNoteFile = async (
    file_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit-report/bi/note-image-delete/${file_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Sign site visit report by building inspector
export const signSiteVisitReportBi = async (
    site_visit_id: number,
    sign: string,
    email_list?: string[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(
            `${baseURL}/api/site-visit-report/bi/sign-report`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: JSON.stringify({
                    site_visit_id,
                    sign,
                    ...(email_list && email_list.length > 0 ? { email_list } : {})
                }),
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Send site visit report via email
export const sendSiteVisitReportViaEmail = async (
    site_visit_id: number,
    email_list: string[]
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for number list
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/site-visit-report/bi/report-email-send`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                site_visit_id,
                ...(email_list && email_list.length > 0 ? { email_list } : {})
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};
/* ******************************************************************* */


/* ******************************************************************* Architect */
// Create/Update response for site visit report note
export const siteVisitReportNoteResponseByArchitect = async (
    site_visit_id: number,
    note_id: number,
    note_response: string,
    date: string,
    file_list: File[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        /* create form data */
        const formData = new FormData();

        formData.append("site_visit_id", String(site_visit_id));
        formData.append("note_id", String(note_id));
        formData.append("response", note_response);
        formData.append("date", date);

        if (file_list && file_list.length > 0) {
            file_list.forEach((file) => {
                formData.append("file_list[]", file);
            });
        }

        const response = await fetch(
            `${baseURL}/api/site-visit-report/architecture/note-response-create-update`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: formData,
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete site visit report note architect response
export const deleteSiteVisitReportNoteArchitectResponse = async (
    response_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit-report/architecture/note-response-delete/${response_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete site visit report note response file
export const deleteSiteVisitReportNoteArchitectResponseFile = async (
    file_id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/site-visit-report/architecture/note-response-image-delete/${file_id}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return { status: false, statusCode: 422, massage: "Body error" };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};
/* ******************************************************************* */